import "../CSS/Faq.css";
import React, { useMemo, useState, useEffect } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import LogoImg from "../multimedia/Logo_universidad_UTM.png";

/* ========= utils ========= */
const slugify = (s) =>
  String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

function toPlainText(node) {
  if (node == null) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(toPlainText).join(" ");
  if (typeof node === "object" && node.props) {
    return toPlainText(node.props.children);
  }
  return "";
}

/* ========= dataset ========= */
const buildFaqItemsRaw = (codigoCurso) => {
  const comunes = [
    {
      q: "¿En qué modalidad se aplica el examen EASI?",
      a: "El examen se realiza de manera presencial o virtual según la naturaleza del curso. Puede seleccionar la fecha, hora y sede a través del Simulador. Las sedes disponibles son: Portoviejo, Bahía de Caráquez, Chone y Quito.",
    },
    {
      q: "¿Qué sucede si no alcanzo el puntaje necesario en el examen?",
      a: "Si no aprueba el examen, puede seleccionar una nueva fecha para rendir la evaluación nuevamente. El proceso de reagendamiento se realizará por el estudiante según la disponibilidad de fechas.",
    },
    {
      q: "¿Qué hago si estoy por egresar y no hay fechas cercanas disponibles?",
      a: "Puede acercarse a la Facultad o Dirección respectiva, de igual manera, escribir al correo electrónico soporte.autoaprendizaje@utm.edu.ec para solicitar una fecha prioritaria. Se evaluará su caso particular según los requisitos de egreso.",
    },
    {
      q: "¿Cuándo debo comenzar las actividades en el Simulador?",
      a: "Se recomienda iniciar las actividades en el Simulador dentro del rango de fechas indicadas por el docente, en especial si es un requisito obligatorio de titulación establecido por la institución.",
    },
  ];

  if (!codigoCurso) return comunes;

  const curso = [
    {
      q: "¿Dónde visualizo el contenido y evaluaciones de este curso?",
      a: `En la página del curso ${codigoCurso} podrá acceder a los niveles, módulos y tareas habilitadas, así como consultar su progreso académico en tiempo real.`,
    },
    {
      q: "Mi curso es de tipo 'examen_solo', ¿dónde rindo el examen?",
      a: (
        <>
          Si el curso <strong>{codigoCurso}</strong> es de modalidad{" "}
          <code>examen_solo</code>, la gestión del examen se realiza en la ruta{" "}
          <code>/curso/{codigoCurso}/examen</code>. En esta sección podrá visualizar
          la fecha, hora y estado de su agendamiento.
        </>
      ),
    },
    {
      q: "¿Cómo funciona el sistema de tutoriales en la plataforma?",
      a: (
        <>
          <p>
            La plataforma educativa incorpora un sistema de tutoriales interactivos
            que se activan automáticamente en tres niveles jerárquicos distintos:
          </p>

          <h4>Tutorial de Nivel 1: Navegación del Curso</h4>
          <ol>
            <li><strong>Introducción a la plataforma:</strong> Presentación del sistema de rutas progresivas y metodología de aprendizaje.</li>
            <li><strong>Encabezado del curso:</strong> Identificación del nombre, código y visualización del progreso global mediante representación circular.</li>
            <li><strong>Sistema de racha:</strong> Reconocimiento a la constancia académica mediante registro diario de avances y recompensas.</li>
            <li><strong>Sistema monetario UTMCoins:</strong> Incentivos acumulables obtenibles al completar tareas y evaluaciones.</li>
            <li><strong>Arquitectura del curso:</strong> Organización jerárquica en niveles, módulos y componentes.</li>
            <li><strong>Inicio de actividades:</strong> Identificación del primer componente habilitado y procedimiento de acceso.</li>
          </ol>

          <h4>Tutorial de Nivel 2: Componentes y Tareas</h4>
          <ol>
            <li><strong>Estructura de componentes:</strong> Funcionamiento del sistema de tareas y actividades.</li>
            <li><strong>Panel de información:</strong> Visualización del nombre del componente, anillo de progreso y condiciones de habilitación.</li>
            <li><strong>Sistema monetario:</strong> Obtención y utilización de UTMCoins.</li>
            <li><strong>Gestión de comodines:</strong> Asignación y adquisición dentro del componente activo.</li>
            <li><strong>Visualización de tareas:</strong> Actividades disponibles, cantidad de ejercicios y acceso a descripciones.</li>
            <li><strong>Sistema de bonificación:</strong> Incentivo económico adicional por completar tareas sin errores al primer intento.</li>
          </ol>

          <h4>Tutorial de Nivel 3: Resolución de Tareas</h4>
          <ol>
            <li><strong>Panel lateral informativo:</strong> Datos de la tarea, tipos de actividades, progreso y recursos.</li>
            <li><strong>Área principal de actividades:</strong> Sistema de vidas y comodines disponibles.</li>
          </ol>

          <h4>Características del sistema tutorial</h4>
          <ul>
            <li>Los tutoriales se visualizan únicamente en el primer acceso.</li>
            <li>Incorporan efectos visuales de enfoque para destacar elementos relevantes.</li>
            <li>El usuario puede omitir el tutorial.</li>
            <li>La información completa está disponible permanentemente en esta sección de ayuda.</li>
          </ul>
        </>
      ),
    },
    {
      q: "¿Cómo funciona el sistema de vidas, comodines y recompensas?",
      a: (
        <>
          <h4>Sistema de Vidas</h4>
          <ul>
            <li>Cada tarea inicia con tres vidas disponibles.</li>
            <li>Cada respuesta incorrecta consume una vida.</li>
            <li>Al llegar a cero vidas no es posible continuar sin recursos adicionales.</li>
            <li>Las vidas se restablecen automáticamente al iniciar una nueva tarea.</li>
          </ul>

          <h4>Tipología de Comodines</h4>
          <ol>
            <li>
              <strong>Pista:</strong>
              <ul>
                <li>Proporciona explicación conceptual de la pregunta.</li>
                <li>No revela directamente la respuesta.</li>
                <li>Su uso no afecta el conteo de vidas.</li>
              </ul>
            </li>
            <li>
              <strong>Recuperación de vida:</strong>
              <ul>
                <li>Restaura una unidad de vida.</li>
                <li>Permite continuar con la tarea.</li>
                <li>Se usa cuando el contador llega a cero.</li>
              </ul>
            </li>
          </ol>

          <h4>Sistema de Recompensas UTMCoins</h4>
          <ul>
            <li><strong>Primer intento exitoso:</strong> multiplicador de 2.5.</li>
            <li><strong>Intentos posteriores sin comodines:</strong> multiplicador de 0.5.</li>
            <li><strong>Intentos con comodines:</strong> valor base.</li>
          </ul>
        </>
      ),
    },
  ];

  return [...curso, ...comunes];
};

export default function Ayuda() {
  const { codigoCurso } = useParams();
  const location = useLocation();
  const { isAuthenticated, user, loading } = useAuth();

  const items = useMemo(() => {
    const raw = buildFaqItemsRaw(codigoCurso);
    return raw.map((it) => ({
      ...it,
      id: slugify(it.q),
      qText: String(it.q || ""),
      aText: toPlainText(it.a),
    }));
  }, [codigoCurso]);

  const [openMap, setOpenMap] = useState({});
  const filtered = items;

  useEffect(() => {
    const hash = location.hash?.replace("#", "");
    if (hash) {
      setOpenMap((prev) => ({ ...prev, [hash]: true }));
      const el = document.getElementById(hash);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, [location.hash]);

  const toggle = (id) => {
    setOpenMap((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const expandAll = () => {
    setOpenMap(Object.fromEntries(filtered.map((it) => [it.id, true])));
  };

  const collapseAll = () => {
    setOpenMap(Object.fromEntries(filtered.map((it) => [it.id, false])));
  };

  const isCourseHelp = Boolean(codigoCurso);
  const showSessionAction = isAuthenticated && user && !loading;

  let actionNode = null;

  if (isCourseHelp) {
    actionNode = (
      <Link to={`/curso/${codigoCurso}`} className="help-login-btn">
        <span>Volver al curso</span>
      </Link>
    );
  } else if (showSessionAction) {
    actionNode = (
      <Link to="/" className="help-login-btn">
        <svg
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M3 12h18" />
          <path d="M13 5l7 7-7 7" />
        </svg>
        <span>Ir al inicio</span>
      </Link>
    );
  } else {
    actionNode = (
      <Link to="/login" className="help-login-btn">
        <svg
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
          <path d="M10 17l5-5-5-5" />
          <path d="M15 12H3" />
        </svg>
        <span className="help-login-btn-text-desktop">Iniciar Sesión</span>
        <span className="help-login-btn-text-mobile">Entrar</span>
      </Link>
    );
  }

  return (
    <div className="help-page-shell">
      <div className="help-page">
        <nav className="help-navbar">
          <div className="help-navbar-inner">
            <div className="help-brand">
              <img
                src={LogoImg}
                alt="Logo UTM"
                className="help-brand-logo"
                loading="eager"
                decoding="async"
                draggable="false"
              />
              <div className="help-brand-text">
                <span className="help-brand-kicker">Sistema de</span>
                <span className="help-brand-title">AutoAprendizaje</span>
              </div>
            </div>

            <div className="help-navbar-actions">
              {actionNode}
            </div>
          </div>
        </nav>

        <main className="help-main">
          <div className="help-hero">
            <h1 className="help-title">
              {isCourseHelp
                ? `Centro de Ayuda - Curso ${codigoCurso}`
                : "Centro de Ayuda Académica"}
            </h1>
            <p className="help-subtitle">
              Documentación técnica y académica del sistema educativo.
            </p>
          </div>

          <div className="help-topbar">
            <div className="help-results">
              <span>{filtered.length}</span> resultados
            </div>

            <div className="help-topbar-actions">
              <button type="button" onClick={expandAll} className="help-pill-btn">
                Expandir todo
              </button>
              <button type="button" onClick={collapseAll} className="help-pill-btn">
                Contraer todo
              </button>
            </div>
          </div>

          <div className="help-faq-list">
            {filtered.map((it) => {
              const isOpen = !!openMap[it.id];

              return (
                <article
                  key={it.id}
                  id={it.id}
                  className={`help-faq-item ${isOpen ? "open" : ""}`}
                >
                  <button
                    type="button"
                    className="help-faq-toggle"
                    onClick={() => toggle(it.id)}
                    aria-expanded={isOpen}
                    aria-controls={`${it.id}-panel`}
                  >
                    <h3 className="help-faq-question">{it.q}</h3>

                    <span className={`help-faq-icon ${isOpen ? "open" : ""}`} aria-hidden="true">
                      <svg
                        width="20"
                        height="20"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </span>
                  </button>

                  <div
                    id={`${it.id}-panel`}
                    className={`help-faq-content ${isOpen ? "open" : ""}`}
                  >
                    <div className="help-faq-answer">{it.a}</div>
                  </div>
                </article>
              );
            })}
          </div>
        </main>

        <div className="help-orb-bottom" />
      </div>
    </div>
  );
}